import { CommonModule, DatePipe } from '@angular/common';
import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, HostListener, OnDestroy, OnInit, signal, ViewChild, ViewEncapsulation } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatNativeDateModule, provideNativeDateAdapter } from '@angular/material/core';
import { MatCalendar, MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialog } from '@angular/material/dialog';
import { MatSidenav, MatSidenavModule } from '@angular/material/sidenav';
import { MatTableDataSource } from '@angular/material/table';
import { MatToolbarModule } from '@angular/material/toolbar';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { TablerIconsModule } from 'angular-tabler-icons';
import { NgScrollbarModule } from 'ngx-scrollbar';
import { ToastrService } from 'ngx-toastr';
import { debounceTime, distinctUntilChanged, map, Observable, of, Subject, switchMap, take, takeUntil } from 'rxjs';
import * as constants from 'src/app/common/constant/constants.type';
import { AppointmentStatusType, AppointmentStatusTypeLabels } from 'src/app/common/enums/appointment-status-type';
import { isPastTime, isTodayOrInThePast } from 'src/app/common/function-helper';
import { NotificationsService } from 'src/app/common/notifications/notifications.service';
import { CommonDropDown } from 'src/app/common/types/dropdown.type';
import { TranslationUtils } from 'src/app/common/utils/translation.utils';
import { LoginService } from 'src/app/login.service';
import { MaterialModule } from 'src/app/material.module';
import { TimeConvertPipe } from 'src/app/pipe/time-convert.pipe';
import { LookupService } from 'src/app/services/lookup.service';
import { SelectionService } from 'src/app/services/selection.service';
import { AddBillChargeComponent } from '../dialogs/bill/bill.component';
import { BillDialogData } from '../dialogs/bill/bill-dialog-data.interface';
import { BillType } from 'src/app/common/enums/bill-type';
import { confirmationDateDialog } from '../dialogs/confirmation-date/confirmation-date.component';
import { DeleteDialog } from '../dialogs/delete.component';
import { OPDService } from '../opd/opd.service';
import { AppointmentService } from './appointment.service';
import { Appointment, AppointmentSlot } from './iappointment';
import { PatientAppointmentHistoryComponent } from './patient-appointment-history/patient-appointment-history.component';
import { AddAppointmentComponent } from './add-appointment/add-appointment.component';
import { AppointmentDialog } from '../dialogs/confirmation-appointment/appointment-dialog.component';
import { PatientReportViewerComponent } from '../dialogs/patient-report-print/patient-report-viewer/patient-report-viewer.component';
import { AppointmentCalendarService, SlotResult } from './appointment-calendar.service';
import { AppointmentPrintService } from './appointment-print.service';

@Component({
  selector: 'app-appointment',
  standalone: true,
  imports: [
    MaterialModule, CommonModule, TablerIconsModule, MatToolbarModule,
    MatSidenavModule, FormsModule, ReactiveFormsModule, MatDatepickerModule,
    MatNativeDateModule, RouterModule, TranslateModule, NgScrollbarModule,
  ],
  providers: [
    DatePipe, TimeConvertPipe, provideNativeDateAdapter(),
    AppointmentCalendarService, AppointmentPrintService,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './appointment.component.html',
  styleUrl: './appointment.component.scss',
  encapsulation: ViewEncapsulation.None
})
export class AppointmentComponent implements OnInit, OnDestroy, AfterViewInit {

  selectedDate: Date = new Date();
  doctorslist: CommonDropDown[];
  selectedDoctorId: number;
  displayedColumns: string[] = [
    'Slots', 'name', 'opdno', 'appointmentType',
    'status', 'value', 'mobile', 'action',
  ];
  dataSource = new MatTableDataSource();
  showInputIndex: number | null = null;
  appointmentAlreadyCreate = false;
  filteredOptions: Observable<CommonDropDown[]>;
  searchTerm$ = new Subject<string>();
  patientID: number;
  currentDoctorId?: number;
  selectedAppointmentTypeId: number;
  statusData = AppointmentStatusTypeLabels;
  statusList = Object.values(AppointmentStatusType).filter(value => typeof value === 'number');
  noSlotsAvailable = false;
  showPatientInfo = false;
  sidePanelOpened = signal(true);
  isIPD = false;

  @ViewChild(MatCalendar) calendar: MatCalendar<Date>;
  @ViewChild('clinicalnav') sidenav!: MatSidenav;

  private destroy$ = new Subject<void>();

  constructor(
    private lookupService: LookupService,
    private datePipe: DatePipe,
    public dialog: MatDialog,
    private router: Router,
    private cdr: ChangeDetectorRef,
    private translate: TranslateService,
    private toastr: ToastrService,
    private opdService: OPDService,
    private route: ActivatedRoute,
    private appointmentService: AppointmentService,
    private timeConvertPipe: TimeConvertPipe,
    protected notificationsService: NotificationsService,
    private loginService: LoginService,
    private selectionService: SelectionService,
    private translationUtils: TranslationUtils,
    private calendarService: AppointmentCalendarService,
    private printService: AppointmentPrintService
  ) { }

  // ── Calendar bindings (arrow functions read live service state) ──

  dateFilter = this.calendarService.dateFilter;
  dateClass = this.calendarService.dateClass;

  ngOnInit(): void {
    this.isIPD = this.loginService.isIPDTabShown();
    this.patientID = Number(this.route.snapshot.paramMap.get('patientId')) || 0;

    this.initDoctorList();
    this.initPatientSearch();
    this.initSignalRListener();
  }

  ngAfterViewInit(): void {
    this.initCalendarStateListener();
    this.handleResponsiveSidenav();
  }

  ngOnDestroy(): void {
    const hospitalId = this.loginService.getHospitalId();
    if (this.selectedDoctorId && hospitalId) {
      this.notificationsService.leaveDoctorCalendarGroup(this.selectedDoctorId, hospitalId);
    }
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ── Public template methods ──

  onChange(doctorId: number) {
    this.noSlotsAvailable = false;
    this.selectedDate = new Date();
    this.selectDoctor(doctorId);
    this.persistSelection();
    this.switchSignalRGroup(doctorId);
    this.calendarService.resetCalendarToDate(this.calendar, this.selectedDate, this.cdr);
    this.loadSlots(this.selectedDate, this.selectedDoctorId);
    this.calendarService.resetBookedDaysCache();
    this.refreshBookedDays();
  }

  onDateChange(date: Date | null) {
    if (!date) return;
    const formattedDate = this.datePipe.transform(date, constants.DATE_FORMAT);
    this.selectedDate = formattedDate ? new Date(formattedDate) : date;
    this.persistSelection();
    this.loadSlots(this.selectedDate, this.selectedDoctorId);
    this.refreshBookedDays();
  }

  filterOptions(value: string) {
    this.searchTerm$.next(value);
  }

  onBillChargeAdd(element: AppointmentSlot) {
    if (element.appointment?.billId) {
      this.openBillChargeDialog(element);
      return;
    }
    const statusNumber = AppointmentStatusType[element.appointment?.status as any as keyof typeof AppointmentStatusType];
    if (statusNumber !== AppointmentStatusType.InProgress) {
      this.openBillChargeDialog(element);
      return;
    }
    this.confirmThenOpenBill(element);
  }

  onAddAppointmentPopup(slot: AppointmentSlot) {
    if (slot.appointment) {
      const statusNumber = AppointmentStatusType[slot.appointment.status as any as keyof typeof AppointmentStatusType];
      if (statusNumber === AppointmentStatusType.Attended) {
        return;
      }

      this.openAppointmentDialog(slot);
      return;
    }
    if (this.isSlotInPast(slot)) {
      this.confirmPastAppointment(slot);
    } else {
      this.bookOrOpenDialog(slot);
    }
  }

  onDeleteAppointment(data: Appointment, event: MouseEvent) {
    event.stopPropagation();
    this.dialog.open(DeleteDialog, {
      width: 'auto', enterAnimationDuration: '300ms', exitAnimationDuration: '300ms', disableClose: true,
      data: { message: this.translate.instant('DELETE_DIALOG') + ' ' + this.translate.instant('ENTITY.APPOINTMENT') + ' ?' }
    }).afterClosed().subscribe((result) => {
      if (result === 'confirm' && data?.appointmentId) {
        this.deleteAppointmentById(data.appointmentId);
      }
    });
  }

  onStartConsultation(element: Appointment, event: MouseEvent) {
    event.stopPropagation();
    this.persistSelection();

    if (!element.opdNumber && element.id == null) {
      this.createOpdAndNavigate(element);
    } else {
      this.router.navigate(['opd', element.patientId, 'consultation', element.opdId]);
    }
  }

  listOfPatientByAppointment(details: Appointment) {
    this.dialog.open(PatientAppointmentHistoryComponent, {
      autoFocus: false, height: 'auto', maxWidth: '90vw',
      data: { patientId: details.patientId, patientName: details.patientName }
    }).afterClosed().subscribe((result) => {
      if (result) this.restoreSelectionAndRefresh();
    });
  }

  listOfPatientReport(details: Appointment) {
    this.dialog.open(PatientReportViewerComponent, {
      autoFocus: false, width: '90vw', height: 'auto', maxWidth: '100vh', maxHeight: '92vh',
      data: { appointmentDetails: details, isDialog: true }
    });
  }

  printAppointmentSchedule() {
    this.printService.printSchedule(
      this.dataSource.data as any[], this.selectedDate,
      this.selectedDoctorId, this.showPatientInfo
    );
  }

  isTodayOrInThePast(date: Date): boolean {
    return isTodayOrInThePast(date);
  }

  isPastTime(timeString: string): boolean {
    return isPastTime(timeString);
  }

  isOver(): boolean {
    return window.innerWidth < 1199;
  }

  @HostListener('window:resize')
  handleResponsiveSidenav() {
    if (window.innerWidth >= 1199) {
      this.sidenav.open();
    } else {
      this.sidenav.close();
    }
  }

  // ── Initialization ──

  private initDoctorList() {
    const storedDoctorId = this.selectionService.value()?.doctorId;
    const storedAppointmentDate = this.selectionService.value()?.appointmentDate;

    this.lookupService.doctors$
      .pipe(takeUntil(this.destroy$))
      .subscribe((data) => {
        this.doctorslist = data.filter(doc => doc.hasSlots);
        if (this.doctorslist.length === 0) {
          this.router.navigate(['/user']);
          this.toastr.warning(this.translate.instant('VALIDATION.CREATE_DOCTOR'));
          return;
        }

        this.resolveInitialDoctor(storedDoctorId);
        this.switchSignalRGroup(this.selectedDoctorId);
        if (storedAppointmentDate) {
          this.selectedDate = new Date(storedAppointmentDate);
        }
        if (storedDoctorId || storedAppointmentDate) this.selectionService.clear();

        this.loadSlots(this.selectedDate, this.selectedDoctorId);
        setTimeout(() => { this.refreshBookedDays(); this.cdr.detectChanges(); }, 300);
        this.cdr.detectChanges();
      });
  }

  private resolveInitialDoctor(storedDoctorId: string | undefined) {
    const isDoctor = this.loginService.isDoctor();
    const currentDoctorId = this.loginService.getUserId();

    const isValid = (id: number) => this.doctorslist.some(d => d.id === id);

    if (storedDoctorId) {
      const id = Number(storedDoctorId);
      if (isValid(id)) {
        this.selectedDoctorId = id;
        return;
      }
    }

    if (isDoctor && currentDoctorId) {
      if (isValid(currentDoctorId)) {
        this.selectedDoctorId = currentDoctorId;
        return;
      }
    }

    this.selectedDoctorId = this.doctorslist[0].id;
  }

  private initPatientSearch() {
    this.filteredOptions = this.searchTerm$.pipe(
      debounceTime(300), distinctUntilChanged(),
      switchMap(term => term?.trim().length > 0
        ? this.lookupService.getPatient(term).pipe(map(r => r || []))
        : of([])
      )
    );
  }

  private initSignalRListener() {
    this.notificationsService.appointmentBooked$
      .pipe(takeUntil(this.destroy$))
      .subscribe((payload: any) => {
        if (!payload) return;
        if (this.selectedDoctorId || payload.appointmentData.doctorId === this.selectedDoctorId) {
          this.refreshAppointmentSlot();
        }
      });
  }

  private initCalendarStateListener() {
    if (!this.calendar) return;

    this.calendar.stateChanges
      .pipe(debounceTime(100), takeUntil(this.destroy$))
      .subscribe(() => this.refreshBookedDays());

    this.calendarService.refreshCalendar(this.calendar, this.cdr);
  }

  // ── Doctor / SignalR ──

  private selectDoctor(doctorId: number) {
    this.selectedDoctorId = doctorId;
    this.cdr.detectChanges();
  }

  private switchSignalRGroup(newDoctorId: number) {
    const hospitalId = Number(this.loginService.getHospitalFromToken());
    if (this.currentDoctorId && hospitalId) {
      this.notificationsService.leaveDoctorCalendarGroup(this.currentDoctorId, hospitalId);
    }
    this.notificationsService.joinDoctorCalendarGroup(newDoctorId, hospitalId);
    this.currentDoctorId = newDoctorId;
  }

  private persistSelection() {
    this.selectionService.set({
      doctorId: this.selectedDoctorId.toString(),
      appointmentDate: this.selectedDate.toISOString()
    });
  }

  // ── Slot loading (delegates to calendar service) ──

  private loadSlots(date: Date, doctorId: number) {
    this.calendarService.fetchSlots(date, doctorId).subscribe(result => {
      this.applySlotResult(result);
    });
  }

  refreshAppointmentSlot() {
    this.calendarService.resetBookedDaysCache();
    this.calendarService.refreshSlots(this.selectedDate, this.selectedDoctorId).subscribe(slots => {
      this.dataSource.data = slots;
      this.refreshBookedDays();
      this.cdr.detectChanges();
    });
  }

  private refreshBookedDays() {
    const activeDate = this.calendar?.activeDate || this.selectedDate || new Date();
    this.calendarService.fetchBookedDays(this.selectedDoctorId, activeDate).subscribe((dataChanged) => {
      if (dataChanged) {
        this.calendarService.refreshCalendar(this.calendar, this.cdr);
      }
    });
  }

  private applySlotResult(result: SlotResult) {
    switch (result.type) {
      case 'slots':
        this.dataSource.data = result.slots!;
        this.noSlotsAvailable = false;
        this.calendarService.updateCalendarActiveDate(this.calendar, this.selectedDate, this.cdr);
        break;
      case 'nextAvailable':
        this.selectedDate = result.selectedDate!;
        this.dataSource.data = result.slots!;
        this.noSlotsAvailable = false;
        this.calendarService.updateCalendarActiveDate(this.calendar, this.selectedDate, this.cdr);
        this.showNextAvailableDateDialog(result.formattedDate!);
        break;
      case 'noAvailability':
        this.dataSource.data = [];
        this.noSlotsAvailable = true;
        break;
      case 'error':
        this.dataSource.data = [];
        this.noSlotsAvailable = true;
        break;
    }
    this.cdr.detectChanges();
  }

  private showNextAvailableDateDialog(availableDate: string) {
    const message = this.translate.instant('APPOINTMENT_RELATED.UNAVALABLE_SLOT', {
      availableDate: this.datePipe.transform(availableDate, constants.DDMMYYYY_FORMAT)
    });

    this.dialog.open(confirmationDateDialog, { data: { message } })
      .afterClosed().subscribe(result => {
        if (result) {
          this.selectedDate = new Date(availableDate);
        }
        this.calendarService.updateCalendarActiveDate(this.calendar, this.selectedDate, this.cdr);
      });
  }

  // ── Appointment actions ──

  private isSlotInPast(slot: AppointmentSlot): boolean {
    const todayStr = new Date().toISOString().split('T')[0];
    const selectedStr = new Date(this.selectedDate).toISOString().split('T')[0];
    return selectedStr < todayStr || (selectedStr === todayStr && this.isPastTime(slot.time));
  }

  private bookOrOpenDialog(slot: AppointmentSlot) {
    if (this.patientID && !this.appointmentAlreadyCreate) {
      this.createDefaultAppointment(slot);
    } else {
      this.openAppointmentDialog(slot);
    }
  }

  private confirmPastAppointment(slot: AppointmentSlot) {
    this.dialog.open(AppointmentDialog, {
      width: 'auto', enterAnimationDuration: '300ms', exitAnimationDuration: '300ms',
      data: { message: this.translate.instant('LABEL.BOOK_PAST_APPOINTMENT'), title: this.translate.instant('ENTITY.APPOINTMENT') }
    }).afterClosed().subscribe((result) => {
      if (result === 'yes') this.bookOrOpenDialog(slot);
    });
  }

  private openAppointmentDialog(slot: AppointmentSlot) {
    this.dialog.open(AddAppointmentComponent, {
      maxWidth: '50vw', disableClose: true,
      data: { event: slot, selectedDate: this.selectedDate, doctorId: this.selectedDoctorId, slots: this.dataSource.data }
    }).afterClosed().subscribe((result) => {
      if (result) this.refreshAppointmentSlot();
    });
  }

  private createDefaultAppointment(slot: AppointmentSlot) {
    this.lookupService.appointmenttype$
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe((types) => {
        const defaultType = types.find(item => item.name === constants.APPOINTMENT_TYPENAME);
        if (defaultType) {
          this.selectedAppointmentTypeId = defaultType.id;
          this.submitDefaultAppointment(slot);
        }
      });
  }

  private submitDefaultAppointment(slot: AppointmentSlot) {
    const data = {
      appointTime: this.timeConvertPipe.transform(slot.time),
      appointDate: this.datePipe.transform(this.selectedDate, constants.DATE_FORMAT)?.toString() || '',
      doctorId: this.selectedDoctorId,
      patientId: this.patientID,
      appointmentTypeId: this.selectedAppointmentTypeId,
      status: AppointmentStatusType.Scheduled,
    };

    this.appointmentService.createAppointment(data).subscribe((response) => {
      if (response.isSuccess) {
        this.toastr.success(this.translationUtils.getTranslatedMessage('COMMON.CREATE', this.translate.instant('ENTITY.APPOINTMENT')));
        this.refreshAppointmentSlot();
        this.appointmentAlreadyCreate = true;
      } else {
        this.toastr.error(Object.values(response.errors || {}).flat().join('\n'));
      }
    });
  }

  private deleteAppointmentById(appointmentId: number) {
    this.appointmentService.deleteAppointment(appointmentId).subscribe((response) => {
      if (response.isSuccess) {
        this.toastr.success(this.translationUtils.getTranslatedMessage('COMMON.DELETE', this.translate.instant('ENTITY.APPOINTMENT')));
        this.refreshAppointmentSlot();
      }
    });
  }

  private confirmThenOpenBill(element: AppointmentSlot) {
    this.dialog.open(AppointmentDialog, {
      width: '400px',
      data: { message: this.translate.instant('APPOINTMENT_RELATED.PRINT_BILL_CONFIRMATION'), title: this.translate.instant('ENTITY.APPOINTMENT') }
    }).afterClosed().subscribe(result => {
      if (result === 'yes') this.openBillChargeDialog(element);
    });
  }

  private openBillChargeDialog(element: AppointmentSlot) {
    const dialogData: BillDialogData = {
      billType: BillType.OPDBill,
      opdBillData: element.appointment || undefined,
      selectedDoctorId: this.selectedDoctorId
    };

    this.dialog.open(AddBillChargeComponent, {
      autoFocus: false, position: { top: '0', right: '0' }, height: '100vh',
      width: '82vw', maxWidth: '1400px', panelClass: ['bill-dialog', 'side-panel'], data: dialogData
    }).afterClosed().subscribe(result => {
      if (result) this.refreshAppointmentSlot();
    });
  }

  private createOpdAndNavigate(element: Appointment) {
    const opdData = {
      patientId: element.patientId, opdDate: this.selectedDate,
      doctorId: this.selectedDoctorId, appointmentId: element.appointmentId,
    };

    this.opdService.createOPD(opdData).subscribe((response) => {
      if (response.isSuccess) {
        this.toastr.success(this.translationUtils.getTranslatedMessage('COMMON.CREATE', this.translate.instant('ENTITY.OPD')));
        this.router.navigate(['/opd', element.patientId, 'consultation', response.result.id]);
      } else {
        this.toastr.error(Object.values(response.errors || {}).flat().join('\n'));
      }
    });
  }

  private restoreSelectionAndRefresh() {
    const storedDoctorId = Number(this.selectionService.value()?.doctorId);
    const storedDateString = this.selectionService.value()?.appointmentDate;
    const storedDate = storedDateString ? new Date(storedDateString) : null;
    if (!storedDoctorId && !storedDate) return;

    this.lookupService.doctors$
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe((data) => {
        this.doctorslist = data.filter(doc => doc.hasSlots);
        if (this.doctorslist.length === 0) return;

        const doc = storedDoctorId ? this.doctorslist.find(d => d.id === storedDoctorId) : undefined;
        this.selectedDoctorId = doc?.id ?? this.doctorslist[0].id;
        if (storedDate) this.selectedDate = new Date(storedDate);
        this.selectionService.clear();
        this.loadSlots(this.selectedDate, this.selectedDoctorId);
        this.cdr.detectChanges();
      });
  }
}
